import { useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import HookFormCheckbox from 'src/components/hookform/HookFormCheckbox';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import BackButton from 'src/components/BackButton';
import Iconify from 'src/components/Iconify';
import StatusChip from 'src/components/StatusChip';
import SuccessButton from 'src/components/SuccessButton';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import { useEnvironmentPoliciesPage } from 'src/hooks/environmentWdacPolicy';
import { formatDate } from 'src/utils/formatTime';
import { getBasePolicyModeColor, getBasePolicyModeLabel } from 'src/utils/enums/basePolicyMode';
import { WDAC_POLICY_TYPE } from 'src/utils/enums/wdacPolicyType';
import { ENVIRONMENT_DEFAULT_HELP } from './EditManagedBasePolicyForm';

const schema = Yup.object().shape({
  envPolicyId: Yup.string().required('Select a base policy to manage'),
  policyDescription: Yup.string().trim(),
  environmentDefault: Yup.boolean(),
});

const DEFAULT_VALUES = { envPolicyId: '', policyDescription: '', environmentDefault: false };

const SelectedBasePolicyCard = ({ policy, showDescription }) => {
  const modeColor = getBasePolicyModeColor(policy.basePolicyMode);

  return (
    <Box
      sx={{
        backgroundColor: '#454545',
        border: '1px solid #5B5B5B',
        borderRadius: '8px',
        p: 1.5,
      }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Iconify icon="mdi:shield-outline" width={18} height={18} color="#87C8E0" />
        <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.88rem' }}>
          {policy.policyName}
        </Typography>
        {modeColor ? (
          <StatusChip
            label={getBasePolicyModeLabel(policy.basePolicyMode)}
            color={modeColor}
            backgroundColor={`${modeColor}1F`}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        ) : null}
      </Stack>
      <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem', mt: 0.25 }}>
        {policy.policyId}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.25,
          mt: 1,
        }}>
        <SummaryValue compact label="Version">
          {policy.policyVersion}
        </SummaryValue>
        <SummaryValue compact label="Devices">
          {policy.devicesCount ?? 0}
        </SummaryValue>
        <SummaryValue compact label="Visibility">
          {policy.isHidden ? 'Hidden' : 'Visible'}
        </SummaryValue>
        <SummaryValue compact label="First seen">
          {formatDate(policy.createdAt, 'yyyy-MM-dd')}
        </SummaryValue>
      </Box>

      {showDescription && policy.policyDescription ? (
        <Typography variant="caption" sx={{ color: '#D1D5DB', display: 'block', mt: 1 }}>
          {policy.policyDescription}
        </Typography>
      ) : null}
    </Box>
  );
};

// Picking a detected base policy. On the managed policies page the settings come as a next step; a
// form generating against the base asks for its description and whether it is the default here,
// once one is picked, and manages it on submit.
const UseOtherBasePolicyForm = ({
  withSettings = false,
  loading,
  submitLabel = 'Continue',
  onCancel,
  onSubmit,
}) => {
  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(schema),
  });

  const { handleSubmit, watch, setValue } = formMethods;

  const { data, isLoading } = useEnvironmentPoliciesPage({
    apiQuery: {
      policyTypes: [WDAC_POLICY_TYPE.BasePolicy],
      isHidden: [false],
      isReported: true,
      isPortalManaged: false,
      sortColumn: 'Devices',
      sortDesc: true,
    },
  });
  const policies = data?.results || [];
  const selectedId = watch('envPolicyId');
  const selected = policies.find(policy => String(policy.id) === selectedId);

  // Each pick starts from the description the policy already has.
  const selectedDescription = selected?.policyDescription || '';
  useEffect(() => {
    setValue('policyDescription', selectedDescription);
  }, [selectedId, selectedDescription, setValue]);

  const submit = values =>
    onSubmit(
      selected,
      withSettings
        ? {
            policyDescription: values.policyDescription?.trim() || null,
            environmentDefault: values.environmentDefault,
          }
        : undefined,
    );

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Stack gap={2}>
        {isLoading ? (
          <Skeleton variant="rounded" height={40} sx={{ bgcolor: '#4B4B4B' }} />
        ) : (
          <HookFormTextField
            name="envPolicyId"
            label="Base policy"
            formMethods={formMethods}
            select
            size="small"
            fullWidth
            disabled={policies.length === 0}
            helperText={
              policies.length === 0
                ? 'Every base policy detected in this environment is already managed.'
                : ''
            }
            options={policies.map(policy => ({
              value: String(policy.id),
              label: policy.policyName,
            }))}
          />
        )}

        {/* With the settings below, the description is edited there instead. */}
        {selected ? (
          <SelectedBasePolicyCard policy={selected} showDescription={!withSettings} />
        ) : null}

        {withSettings && selected ? (
          <>
            <HookFormTextField
              name="policyDescription"
              label="Policy description"
              formMethods={formMethods}
              placeholder="Optional"
              size="small"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
            />
            <HookFormCheckbox
              name="environmentDefault"
              label="Environment default"
              helperText={ENVIRONMENT_DEFAULT_HELP}
              formMethods={formMethods}
            />
          </>
        ) : null}

        {withSettings ? null : (
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            The next step sets what the portal does with this base policy. It is not managed until
            you save there, and nothing about the policy itself changes.
          </Typography>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BackButton disabled={loading} onClick={onCancel}>
            Cancel
          </BackButton>
          <SuccessButton type="submit" disabled={loading || !selected} loading={loading}>
            {submitLabel}
          </SuccessButton>
        </Stack>
      </Stack>
    </form>
  );
};

UseOtherBasePolicyForm.propTypes = {
  withSettings: PropTypes.bool,
  loading: PropTypes.bool,
  submitLabel: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default UseOtherBasePolicyForm;
